import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, Copy, Star, Share2, Shuffle, Trash2, Loader2, ChevronDown, ChevronUp, BookOpen, Trophy, Sparkles, Filter } from "lucide-react";

const LS_FAVS = "jixxeo_unscramble_favs";
const LS_WOTD = "jixxeo_unscramble_wotd";

/**
 * Jixxeo Word Unscrambler — everything lives in one page for offline-first
 * simplicity. Dictionary is dynamic-imported so the initial JS chunk stays
 * tiny; results are computed with a single anagram-hash pass over ~370K
 * English words and cached per query.
 */
export default function WordUnscramblerApp() {
  const [dict, setDict] = useState(null);       // { words: [], byHash: Map<hash,[word]> }
  const [dictBusy, setDictBusy] = useState(true);
  const [letters, setLetters] = useState("");
  const [minLen, setMinLen] = useState(3);
  const [maxLen, setMaxLen] = useState(15);
  const [startsWith, setStartsWith] = useState("");
  const [endsWith, setEndsWith] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [sortAZ, setSortAZ] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);
  const [favs, setFavs] = useState(() => JSON.parse(localStorage.getItem(LS_FAVS) || "[]"));
  const [wotd, setWotd] = useState(() => JSON.parse(localStorage.getItem(LS_WOTD) || "null"));
  useEffect(() => { document.title = "Jixxeo Word Unscrambler"; }, []);

  // Load dictionary once, then index by anagram-hash for O(1) lookup
  useEffect(() => {
    (async () => {
      try {
        const mod = await import("an-array-of-english-words");
        const words = mod.default || mod;
        const byHash = new Map();
        // For every word, group into "all subset anagrams" is O(2^n) — too big.
        // Instead we compute a canonical letter-count key per word and match
        // dynamically by iterating words <= letters.length. Simple + fast.
        setDict({ words, byHash });
      } catch (e) {
        toast.error("Couldn't load dictionary");
      } finally { setDictBusy(false); }
    })();
  }, []);

  // Fetch Word of the Day (once per calendar day)
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (wotd?.date === today && wotd?.definition) return;
    if (!dict) return;
    const w = pickWordOfTheDay(dict.words, today);
    (async () => {
      try {
        const r = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(w)}`);
        if (!r.ok) throw new Error("no def");
        const j = await r.json();
        const entry = j?.[0];
        const meaning = entry?.meanings?.[0]?.definitions?.[0];
        const payload = {
          date: today, word: w,
          phonetic: entry?.phonetic || entry?.phonetics?.find((p) => p.text)?.text || "",
          audio: entry?.phonetics?.find((p) => p.audio)?.audio || "",
          definition: meaning?.definition || "A word in the English dictionary.",
          example: meaning?.example || "",
          partOfSpeech: entry?.meanings?.[0]?.partOfSpeech || "",
        };
        localStorage.setItem(LS_WOTD, JSON.stringify(payload));
        setWotd(payload);
      } catch {
        const payload = { date: today, word: w, definition: "Look this one up — it's a lesser-known gem!", example: "", partOfSpeech: "" };
        setWotd(payload); localStorage.setItem(LS_WOTD, JSON.stringify(payload));
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dict]);

  const clean = letters.toLowerCase().replace(/[^a-z?*]/g, "");
  const results = useMemo(() => {
    if (!dict || !clean) return null;
    return findWords(dict.words, clean, {
      minLen, maxLen,
      startsWith: startsWith.trim().toLowerCase(),
      endsWith: endsWith.trim().toLowerCase(),
    });
  }, [dict, clean, minLen, maxLen, startsWith, endsWith]);

  const grouped = useMemo(() => {
    if (!results) return null;
    const g = new Map();
    for (const w of results) {
      if (!g.has(w.length)) g.set(w.length, []);
      g.get(w.length).push(w);
    }
    const keys = [...g.keys()].sort((a, b) => sortAZ ? a - b : b - a);
    for (const k of keys) g.get(k).sort();
    return keys.map((k) => [k, g.get(k)]);
  }, [results, sortAZ]);

  const shuffle = () => setLetters([...clean].sort(() => Math.random() - 0.5).join("").toUpperCase());
  const clear = () => { setLetters(""); setStartsWith(""); setEndsWith(""); };
  const toggleFav = (word) => {
    setFavs((prev) => {
      const next = prev.includes(word) ? prev.filter((x) => x !== word) : [...prev, word];
      localStorage.setItem(LS_FAVS, JSON.stringify(next));
      return next;
    });
  };
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); } };
  const copyAll = () => results && copy(results.join("\n"));
  const share = async () => {
    const text = results?.length ? `From "${letters.toUpperCase()}" (${results.length} words):\n${results.slice(0, 30).join(", ")}\n\nJixxeo Word Unscrambler` : "Try Jixxeo Word Unscrambler";
    try { if (navigator.share) { await navigator.share({ title: "Words", text }); return; } } catch (e) { if (e?.name === "AbortError") return; }
    copy(text);
  };

  return (
    <div data-testid="unscrambler-app" className="min-h-screen bg-gradient-to-b from-[#001A29] via-[#001220] to-[#000A15] text-[#EAF7FF]" style={{ fontFamily: "'Cabinet Grotesk','Outfit',ui-sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#001220]/85 border-b border-[#00F0FF]/25">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/browse" data-testid="unscramble-back" className="p-2 -ml-2 rounded-full hover:bg-white/10"><ArrowLeft className="w-4 h-4" /></Link>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-xl bg-[#00F0FF]/15 text-[#00F0FF] flex items-center justify-center"><Sparkles className="w-5 h-5" /></div>
            <div>
              <div className="font-black text-lg leading-none">Jixxeo <span className="text-[#00F0FF]">Word</span></div>
              <div className="text-[9px] uppercase tracking-widest text-white/40">Unscrambler</div>
            </div>
          </div>
          <button data-testid="unscramble-challenge-btn" onClick={() => setShowChallenge(true)}
            className="inline-flex items-center gap-1 bg-[#FFD700] text-black font-black text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full">
            <Trophy className="w-3 h-3" /> Race
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 pb-28 space-y-5">
        {/* Letter tiles preview */}
        {clean && (
          <div data-testid="letter-tiles" className="flex flex-wrap gap-1.5 justify-center">
            {clean.split("").map((c, i) => (
              <div key={i} className="w-10 h-12 sm:w-11 sm:h-14 rounded-xl bg-gradient-to-br from-[#00F0FF]/25 to-[#0EA5E9]/15 border border-[#00F0FF]/40 flex items-center justify-center font-black text-2xl text-[#00F0FF] shadow-[0_0_12px_rgba(0,240,255,0.25)]" style={{ animation: `pop .35s ${i * 30}ms backwards` }}>
                {c === "?" || c === "*" ? "•" : c.toUpperCase()}
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <section className="rounded-3xl border border-[#00F0FF]/30 bg-black/30 backdrop-blur p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Enter your letters</div>
          <div className="flex gap-2">
            <input data-testid="letters-input" value={letters} onChange={(e) => setLetters(e.target.value.slice(0, 15))}
              placeholder="Type letters — e.g. RAEHT"
              className="flex-1 bg-transparent border-b border-white/20 focus:border-[#00F0FF] outline-none py-3 text-2xl font-black tracking-widest uppercase" />
            {letters && <button data-testid="letters-clear" onClick={clear} className="p-2 text-white/60 hover:text-white"><Trash2 className="w-4 h-4" /></button>}
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            <button data-testid="unscramble-btn" onClick={() => letters && letters.length}
              className="flex-1 min-w-[8rem] inline-flex items-center justify-center gap-1 bg-[#00F0FF] text-black font-black text-sm uppercase tracking-widest px-4 py-2.5 rounded-full shadow-[0_0_18px_rgba(0,240,255,0.5)]">
              <Search className="w-3.5 h-3.5" /> Unscramble
            </button>
            <button data-testid="letters-shuffle" onClick={shuffle} disabled={!clean}
              className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full disabled:opacity-40">
              <Shuffle className="w-3.5 h-3.5" /> Shuffle
            </button>
            <button data-testid="unscramble-advanced" onClick={() => setAdvanced((v) => !v)}
              className="inline-flex items-center gap-1 border border-[#00F0FF]/30 bg-[#00F0FF]/10 text-[#00F0FF] font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full">
              <Filter className="w-3.5 h-3.5" /> Advanced {advanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
          <div className="text-[10px] text-white/40 mt-2">Use <b>?</b> or <b>*</b> as a wildcard for any letter.</div>

          {advanced && (
            <div data-testid="advanced-panel" className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <label className="col-span-2"><span className="text-white/50">Starts with</span>
                <input data-testid="starts-with" value={startsWith} onChange={(e) => setStartsWith(e.target.value)} maxLength={4}
                  className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-lg px-3 py-2 mt-1 uppercase tracking-widest" /></label>
              <label className="col-span-2"><span className="text-white/50">Ends with</span>
                <input data-testid="ends-with" value={endsWith} onChange={(e) => setEndsWith(e.target.value)} maxLength={4}
                  className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-lg px-3 py-2 mt-1 uppercase tracking-widest" /></label>
              <label><span className="text-white/50">Min length</span>
                <input data-testid="min-len" type="number" min={2} max={15} value={minLen} onChange={(e) => setMinLen(Number(e.target.value) || 3)}
                  className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-lg px-3 py-2 mt-1" /></label>
              <label><span className="text-white/50">Max length</span>
                <input data-testid="max-len" type="number" min={2} max={15} value={maxLen} onChange={(e) => setMaxLen(Number(e.target.value) || 15)}
                  className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-lg px-3 py-2 mt-1" /></label>
            </div>
          )}
        </section>

        {/* Results */}
        <section data-testid="results">
          {dictBusy ? (
            <div className="py-10 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading dictionary…</div>
          ) : !clean ? (
            <div className="text-center text-sm text-white/40 py-6">Type a few letters to start.</div>
          ) : !results?.length ? (
            <div className="text-center text-sm text-white/50 py-6" data-testid="results-empty">No words found. Try more letters or wildcards.</div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <div className="font-black text-lg" data-testid="results-count">Words found: <span className="text-[#00F0FF]">{results.length}</span></div>
                <div className="ml-auto flex gap-2">
                  <button data-testid="sort-toggle" onClick={() => setSortAZ((v) => !v)}
                    className="text-[10px] font-bold uppercase tracking-widest bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
                    {sortAZ ? "Longest first" : "A–Z"}
                  </button>
                  <button data-testid="copy-all-btn" onClick={copyAll}
                    className="text-[10px] font-bold uppercase tracking-widest bg-[#00F0FF] text-black px-3 py-1.5 rounded-full">
                    <Copy className="w-3 h-3 inline mr-0.5" /> Copy all
                  </button>
                  <button data-testid="share-btn" onClick={share}
                    className="text-[10px] font-bold uppercase tracking-widest border border-white/20 bg-white/5 px-3 py-1.5 rounded-full">
                    <Share2 className="w-3 h-3 inline mr-0.5" /> Share
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                {grouped.map(([len, words]) => (
                  <div key={len} data-testid={`group-${len}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#00F0FF] font-bold mb-1">{len}-Letter Words · {words.length}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {words.map((w) => (
                        <div key={w} data-testid={`word-${w}`} className="flex items-center bg-white/5 border border-white/10 hover:border-[#00F0FF]/50 rounded-full overflow-hidden">
                          <button onClick={() => copy(w)}
                            className="px-3 py-1.5 text-sm font-black uppercase tracking-wider hover:bg-white/10">
                            {w}
                          </button>
                          <button data-testid={`fav-${w}`} onClick={() => toggleFav(w)}
                            className={"px-2 py-1.5 border-l border-white/10 " + (favs.includes(w) ? "text-[#FFD700]" : "text-white/30 hover:text-[#FFD700]")}>
                            <Star className="w-3 h-3" fill={favs.includes(w) ? "currentColor" : "none"} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        {/* Word of the Day */}
        {wotd && (
          <section data-testid="wotd" className="rounded-3xl border border-[#7A00FF]/40 bg-gradient-to-br from-[#7A00FF]/20 via-black/40 to-transparent p-5">
            <div className="text-[10px] uppercase tracking-widest text-[#B47BFF] font-bold flex items-center gap-1"><BookOpen className="w-3 h-3" /> Word of the day</div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <div className="font-black text-3xl text-[#B47BFF]" data-testid="wotd-word">{wotd.word.toUpperCase()}</div>
              {wotd.phonetic && <div className="text-white/40 text-sm">{wotd.phonetic}</div>}
              {wotd.partOfSpeech && <div className="text-[10px] uppercase tracking-widest bg-white/10 px-2 py-0.5 rounded-full">{wotd.partOfSpeech}</div>}
            </div>
            <div className="text-sm text-white/80 mt-2">{wotd.definition}</div>
            {wotd.example && <div className="text-xs text-white/50 italic mt-1">"{wotd.example}"</div>}
            <div className="mt-3 flex gap-2">
              <button data-testid="wotd-learn" onClick={() => { toggleFav(wotd.word); toast.success("Saved to My Vocabulary"); }}
                className="inline-flex items-center gap-1 bg-[#7A00FF] text-white font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full">
                <Star className="w-3.5 h-3.5" /> Learn this word
              </button>
              {wotd.audio && (
                <button onClick={() => new Audio(wotd.audio).play()}
                  className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full">
                  🔊 Listen
                </button>
              )}
            </div>
          </section>
        )}

        {/* Vocabulary */}
        {favs.length > 0 && (
          <section data-testid="vocab-list">
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">My Vocabulary · {favs.length}</div>
            <div className="flex flex-wrap gap-1.5">
              {favs.slice(0, 60).map((w) => (
                <div key={w} className="inline-flex items-center bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full overflow-hidden">
                  <span className="px-3 py-1 text-sm font-black uppercase tracking-wider text-[#FFD700]">{w}</span>
                  <button onClick={() => toggleFav(w)} className="px-2 py-1 border-l border-[#FFD700]/20 text-white/40 hover:text-[#F87171]"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {showChallenge && dict && <ChallengeModal dict={dict} onClose={() => setShowChallenge(false)} />}

      <style>{`@keyframes pop { from { transform: translateY(10px) scale(0.7); opacity: 0 } to { transform: none; opacity: 1 } }`}</style>
    </div>
  );
}

/**
 * Anagram-subset filter. Returns every dictionary word whose letter-count
 * histogram is a subset of `letters` (with '?' and '*' as wildcards),
 * respecting min/max length + starts/ends constraints.
 */
function findWords(words, letters, { minLen, maxLen, startsWith, endsWith }) {
  const letterCounts = {};
  let wildcards = 0;
  for (const c of letters) {
    if (c === "?" || c === "*") wildcards++;
    else letterCounts[c] = (letterCounts[c] || 0) + 1;
  }
  const total = letters.length;
  const out = [];
  for (const w of words) {
    if (w.length < minLen || w.length > maxLen || w.length > total) continue;
    if (startsWith && !w.startsWith(startsWith)) continue;
    if (endsWith && !w.endsWith(endsWith)) continue;
    const need = {};
    for (const c of w) need[c] = (need[c] || 0) + 1;
    let wc = wildcards;
    let ok = true;
    for (const c in need) {
      const have = letterCounts[c] || 0;
      if (need[c] > have) {
        wc -= need[c] - have;
        if (wc < 0) { ok = false; break; }
      }
    }
    if (ok) out.push(w);
  }
  out.sort((a, b) => b.length - a.length || a.localeCompare(b));
  // Cap huge lists so the DOM stays snappy
  return out.slice(0, 500);
}

function pickWordOfTheDay(words, dateKey) {
  // Deterministic per-day pick from words that are 5–8 letters and look nice.
  const pool = words.filter((w) => w.length >= 5 && w.length <= 8);
  let h = 0; for (let i = 0; i < dateKey.length; i++) h = (h * 31 + dateKey.charCodeAt(i)) | 0;
  return pool[Math.abs(h) % pool.length];
}

function ChallengeModal({ dict, onClose }) {
  const [scrambled, setScrambled] = useState("");
  const [answer, setAnswer] = useState("");
  const [input, setInput] = useState("");
  const [found, setFound] = useState([]);
  const [timeLeft, setTimeLeft] = useState(60);
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => { newChallenge(); return () => clearInterval(timerRef.current); }, []);
  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => { if (t <= 1) { clearInterval(timerRef.current); setRunning(false); return 0; } return t - 1; });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  const newChallenge = () => {
    const pool = dict.words.filter((w) => w.length === 6);
    const w = pool[Math.floor(Math.random() * pool.length)];
    setAnswer(w);
    setScrambled([...w].sort(() => Math.random() - 0.5).join(""));
    setFound([]); setInput(""); setTimeLeft(60); setRunning(true);
  };

  const check = (e) => {
    e.preventDefault();
    const guess = input.trim().toLowerCase();
    if (!guess || found.includes(guess) || guess.length < 3) return setInput("");
    // must be valid word + use only letters we have (letter-count subset)
    const need = {}; for (const c of guess) need[c] = (need[c] || 0) + 1;
    const have = {}; for (const c of answer) have[c] = (have[c] || 0) + 1;
    for (const c in need) if ((have[c] || 0) < need[c]) return setInput("");
    if (!dict.words.includes(guess)) return setInput("");
    setFound((f) => [...f, guess]);
    setInput("");
    if (guess === answer) { setRunning(false); toast.success("You cracked it!"); }
  };

  return (
    <div data-testid="challenge-modal" onClick={onClose}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-3xl border border-[#FFD700]/50 bg-gradient-to-br from-[#001220] via-[#001a29] to-[#000] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-5 h-5 text-[#FFD700]" fill="currentColor" />
          <div className="font-black text-lg">Unscramble Race</div>
          <div className="ml-auto font-black text-lg tabular-nums" style={{ color: timeLeft < 10 ? "#F87171" : "#00F0FF" }} data-testid="challenge-timer">{timeLeft}s</div>
        </div>
        <div className="text-center py-4">
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Find the 6-letter word</div>
          <div className="flex justify-center gap-1.5" data-testid="challenge-scrambled">
            {scrambled.split("").map((c, i) => (
              <div key={i} className="w-10 h-12 rounded-xl bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center font-black text-2xl text-[#FFD700]">{c.toUpperCase()}</div>
            ))}
          </div>
        </div>
        <form onSubmit={check} className="flex gap-2">
          <input data-testid="challenge-input" value={input} onChange={(e) => setInput(e.target.value.slice(0, 15))} disabled={!running}
            placeholder="Type a word…" className="flex-1 bg-white/5 border border-white/10 focus:border-[#FFD700] rounded-full px-4 py-2 uppercase tracking-widest outline-none disabled:opacity-40" />
          <button type="submit" disabled={!running} className="bg-[#FFD700] text-black font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full disabled:opacity-40">Enter</button>
        </form>
        <div className="mt-3 min-h-[3rem] flex flex-wrap gap-1.5" data-testid="challenge-found">
          {found.map((w) => (
            <span key={w} className={"px-2 py-1 rounded-full text-xs font-bold uppercase " + (w === answer ? "bg-[#39FF14] text-black" : "bg-white/10")}>{w}</span>
          ))}
        </div>
        {!running && (
          <div className="mt-3 text-center">
            <div className="text-sm text-white/70">The word was <b className="text-[#FFD700]">{answer.toUpperCase()}</b>.</div>
            <div className="mt-2 flex justify-center gap-2">
              <button data-testid="challenge-new" onClick={newChallenge} className="bg-[#00F0FF] text-black font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full">New round</button>
              <button data-testid="challenge-close" onClick={onClose} className="border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
